/**
 * AsyncState — 기다림을 보여주는 세 가지 기본 조각
 *
 *   <Skeleton lines={3} />                         텍스트 줄 스켈레톤
 *   <Skeleton variant="block" height={120} />      블록 스켈레톤 (카드·이미지 자리)
 *   <Spinner size={24} label="불러오는 중" />       스피너 (2px, 24px 초과는 3px)
 *   <Progress value={40} label="3개 중 2개 완료" onCancel={stop} />
 *
 * Progress 규칙
 *   - value(0~100)가 실제 숫자일 때만 determinate. 그 외에는 indeterminate 바를 보여준다.
 *   - 가짜 진행률을 만들지 않는다. indeterminate에는 aria-valuenow도 붙이지 않는다.
 *   - label은 "무엇을 하고 있는지"를 텍스트로 설명한다 (모션에만 의존하지 않는다).
 *   - onCancel이 있으면 "취소" 텍스트 버튼이 함께 보인다.
 *
 * 모든 모션은 prefers-reduced-motion을 존중한다.
 */

import React from 'react';
import './AsyncState.css';

const joinClassNames = (...values) => values.filter(Boolean).join(' ');

const toCssSize = (value) => (typeof value === 'number' ? `${value}px` : value);

const SPINNER_SIZES = { sm: 16, md: 24, lg: 32, xl: 48 };

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

export const Skeleton = ({
  className,
  height,
  lines = 1,
  radius,
  style,
  variant = 'text',
  width,
}) => {
  const baseStyle = {
    ...(width !== undefined ? { width: toCssSize(width) } : {}),
    ...(height !== undefined ? { height: toCssSize(height) } : {}),
    ...(radius !== undefined ? { borderRadius: toCssSize(radius) } : {}),
    ...style,
  };

  if (variant === 'text' && lines > 1) {
    return (
      <div
        aria-hidden="true"
        className={joinClassNames('whatquiz-skeleton-lines', className)}
        data-testid="skeleton"
      >
        {Array.from({ length: lines }, (_, index) => (
          <span
            className="whatquiz-skeleton whatquiz-skeleton--text"
            data-testid="skeleton-line"
            key={index}
            style={{
              ...baseStyle,
              // 마지막 줄은 짧게 — 실제 문단처럼 보이게
              ...(index === lines - 1 && width === undefined ? { width: '62%' } : {}),
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <span
      aria-hidden="true"
      className={joinClassNames('whatquiz-skeleton', `whatquiz-skeleton--${variant}`, className)}
      data-testid="skeleton"
      style={baseStyle}
    />
  );
};

// ---------------------------------------------------------------------------
// Spinner
// ---------------------------------------------------------------------------

export const Spinner = ({
  className,
  label,
  size = 'md',
  thickness,
  tone = 'primary',
}) => {
  const px = typeof size === 'number' ? size : (SPINNER_SIZES[size] || SPINNER_SIZES.md);
  const borderWidth = typeof thickness === 'number' ? thickness : (px > 24 ? 3 : 2);
  const hasLabel = typeof label === 'string' && label.trim().length > 0;

  return (
    <span
      aria-hidden={hasLabel ? undefined : 'true'}
      aria-label={hasLabel ? label : undefined}
      className={joinClassNames('whatquiz-spinner', `whatquiz-spinner--${tone}`, className)}
      data-testid="spinner"
      role={hasLabel ? 'status' : undefined}
      style={{ width: px, height: px, borderWidth }}
    />
  );
};

// ---------------------------------------------------------------------------
// Progress
// ---------------------------------------------------------------------------

const clampPercent = (value) => Math.max(0, Math.min(100, value));

export const isDeterminateProgress = (value) => (
  typeof value === 'number' && Number.isFinite(value)
);

export const Progress = ({
  'aria-label': ariaLabel = '진행 상황',
  cancelLabel = '취소',
  className,
  label,
  onCancel,
  showPercent = true,
  value = null,
}) => {
  const determinate = isDeterminateProgress(value);
  const percent = determinate ? Math.round(clampPercent(value)) : null;
  const hasLabel = typeof label === 'string' && label.trim().length > 0;
  const percentText = determinate && showPercent ? `${percent}%` : null;
  const valueText = hasLabel ? label : (percentText || undefined);
  const showMeta = hasLabel || percentText || typeof onCancel === 'function';

  return (
    <div
      className={joinClassNames(
        'whatquiz-progress',
        determinate ? 'whatquiz-progress--determinate' : 'whatquiz-progress--indeterminate',
        className,
      )}
      data-testid="progress"
    >
      <div
        aria-label={ariaLabel}
        aria-valuemax={100}
        aria-valuemin={0}
        aria-valuenow={determinate ? percent : undefined}
        aria-valuetext={valueText}
        className="whatquiz-progress__track"
        role="progressbar"
      >
        <div
          className="whatquiz-progress__bar"
          data-testid="progress-bar"
          style={determinate ? { width: `${percent}%` } : undefined}
        />
      </div>
      {showMeta && (
        <div className="whatquiz-progress__meta">
          <span className="whatquiz-progress__label">
            {hasLabel ? label : null}
            {hasLabel && percentText ? <span className="whatquiz-progress__dot" aria-hidden="true">·</span> : null}
            {percentText ? <span className="whatquiz-progress__percent">{percentText}</span> : null}
          </span>
          {typeof onCancel === 'function' && (
            <button
              className="whatquiz-progress__cancel"
              data-testid="progress-cancel"
              onClick={onCancel}
              type="button"
            >
              {cancelLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const AsyncState = { Skeleton, Spinner, Progress };

export default AsyncState;
